import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { createWhatsAppLink } from "../config/site";

/* -------------------------------- Helpers ------------------------------- */
const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 240' role='img' aria-label='No image'>
      <defs>
        <linearGradient id='g' x1='0' x2='1'>
          <stop offset='0' stop-color='#f6f7f8'/>
          <stop offset='0.5' stop-color='#edeef1'/>
          <stop offset='1' stop-color='#f6f7f8'/>
        </linearGradient>
      </defs>
      <rect width='100%' height='100%' fill='url(#g)'/>
      <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
        font-family='system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial' font-size='14' fill='#9aa0a6'>
        No image
      </text>
    </svg>`
  );

const isAbs = (u) => /^https?:\/\//i.test(u || "");
const toAbs = (u) => {
  if (!u) return u;
  if (isAbs(u)) return u;
  if (
    u.startsWith("/images") ||
    u.startsWith("/public/") ||
    u.startsWith("/shop/routes/uploads")
  ) {
    const origin =
      typeof window !== "undefined" && window.location ? window.location.origin : "";
    return origin + u;
  }
  return u;
};

const norm = (s) => (s ?? "").toString().trim();

const formatKES = (n) =>
  n == null || n === ""
    ? null
    : `KES ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const cleanSizes = (sizes) => {
  if (!Array.isArray(sizes)) return [];
  const parsed = sizes
    .map((v) => (typeof v === "number" ? v : parseInt(v, 10)))
    .filter((v) => Number.isFinite(v));
  if (parsed.length === 0) {
    return Array.from(new Set(sizes.map((s) => String(s).trim()).filter(Boolean)));
  }
  const unique = Array.from(new Set(parsed)).sort((a, b) => a - b);
  return unique;
};

const sizeText = (sizes) => {
  if (!Array.isArray(sizes) || !sizes.length) return null;
  if (sizes.every((v) => Number.isFinite(v))) {
    const min = sizes[0];
    const max = sizes[sizes.length - 1];
    return min === max ? `${min}` : `${min} - ${max}`;
  }
  return sizes.join(" / ");
};

/* ------------------------------ Size Modal UI ----------------------------- */
function SizeModal({ open, onClose, sizes, onConfirm }) {
  const numeric = sizes.every((v) => Number.isFinite(v));
  const [picked, setPicked] = useState(() => (numeric ? sizes[0] : sizes[0] || ""));

  useEffect(() => {
    if (!open) return;
    setPicked(numeric ? sizes[0] : sizes[0] || "");
  }, [open, numeric, sizes]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter") onConfirm?.(picked);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, picked, onClose, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="Select a size" className="relative w-full sm:max-w-md rounded-3xl bg-white shadow-2xl border border-gray-100 p-6 sm:p-7 mx-2">
        <div className="mb-4">
          <h3 className="text-lg font-semibold tracking-tight">Choose your size</h3>
          <p className="text-sm text-gray-500">Select one option then confirm to add to cart.</p>
        </div>

        {numeric ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span>Range</span>
              <span className="font-medium">{sizes[0]} – {sizes[sizes.length - 1]}</span>
            </div>
            <input type="range" min={sizes[0]} max={sizes[sizes.length - 1]} step={1} value={Number(picked)} onChange={(e) => setPicked(parseInt(e.target.value, 10))} className="w-full accent-black" />
            <div className="grid grid-cols-5 gap-2 max-h-36 overflow-auto pr-1">
              {sizes.map((s) => (
                <button key={s} type="button" onClick={() => setPicked(s)} className={"text-sm rounded-xl px-3 py-2 border transition " + (Number(picked) === s ? "bg-black text-white border-black" : "bg-white hover:bg-gray-50 border-gray-300")}>{s}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {sizes.map((s) => (
              <button key={s} type="button" onClick={() => setPicked(s)} className={"text-sm rounded-xl px-3 py-2 border transition " + (picked === s ? "bg-black text-white border-black" : "bg-white hover:bg-gray-50 border-gray-300")}>{s}</button>
            ))}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={() => onConfirm?.(picked)} className="px-4 py-2 rounded-xl bg-black text-white text-sm hover:opacity-90">Confirm</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Main Component --------------------------- */
export default function ProductCard({ product, onAdd, onDetails }) {
  const navigate = useNavigate();
  const { add } = useCart(); // <-- call the CartContext directly to guarantee add

  const name = norm(product?.name);
  const color = norm(product?.color);
  const priceText = formatKES(product?.price);

  const sizes = useMemo(() => cleanSizes(product?.sizes || []), [product?.sizes]);
  const hasSizes = sizes.length > 0;
  const sizesLabel = sizeText(sizes);

  const src = toAbs(product?.image) || PLACEHOLDER;
  const alt = [name, color && `– ${color}`].filter(Boolean).join(" ");

  const [imgFailed, setImgFailed] = useState(false);
  const handleImgError = (e) => {
    if (imgFailed) return;
    setImgFailed(true);
    e.currentTarget.src = PLACEHOLDER;
  };

  const [open, setOpen] = useState(false);

  const pushToCart = (selectedSize) => {
    // Always add via context (and still call parent onAdd if provided)
    const payload = {
      ...product,
      size: selectedSize ?? null, // normalized field name
      selectedSize: selectedSize ?? null,
      qty: 1,
    };
    try { add(payload); } catch (_) {}
    try { onAdd?.(payload); } catch (_) {}

    // Ensure navigation happens after state commit
    setTimeout(() => {
      try {
        navigate("/cart", { replace: false });
      } catch (_) {
        window.location.href = "/cart";
      }
    }, 0);
  };

  const startOrder = () => {
    if (!hasSizes) {
      pushToCart(null);
      return;
    }
    setOpen(true);
  };

  const confirmOrder = (picked) => {
    setOpen(false);
    pushToCart(picked);
  };

  return (
    <article className="group relative rounded-3xl border border-gray-100 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="aspect-[4/3] bg-gradient-to-b from-white to-gray-50 flex items-center justify-center">
        <img src={src} alt={alt} loading="lazy" onError={handleImgError} className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]" />
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>Sizes: <strong>{sizesLabel ? sizesLabel : "—"}</strong></span>
          <span className="inline-flex items-center rounded-full border px-2 py-[3px] text-[11px] bg-white" aria-label={`color ${color || "unspecified"}`} title={color || "—"}>{color || "—"}</span>
        </div>

        <h3 className="mt-1 font-semibold tracking-tight truncate" title={name}>{name || "Unnamed"}</h3>

        <div className="mt-1 text-[15px]">{priceText ?? <span className="text-gray-500">Contact</span>}</div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          <button type="button" className="inline-flex items-center gap-2 rounded-2xl bg-black text-white px-4 py-2 text-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-black/30" onClick={startOrder}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4h-2l-3 7v2h2l3-7h12l2 5h-13l-1 2h14v2h-16l-1 2h-3v2h4l1-2h13l3-7-3-6h-14z"/></svg>
            Order
          </button>

          <a href={createWhatsAppLink(`Hi! I'm interested in ${name}${color ? ` (${color})` : ""}. Price: ${priceText ?? "N/A"}`)} target="_blank" rel="noreferrer" className="rounded-2xl border px-4 py-2 text-sm hover:bg-gray-50">WhatsApp</a>

          <button type="button" className="ml-auto text-sm text-gray-600 hover:text-black" onClick={() => onDetails?.(product)}>Details</button>
        </div>
      </div>

      {/* Size Picker Modal */}
      <SizeModal open={open} onClose={() => setOpen(false)} sizes={sizes} onConfirm={confirmOrder} />
    </article>
  );
}
